import React, { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import "leaflet/dist/leaflet.css";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  CircleMarker,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import RecenterButton from "../components/RecenterButton";
import AdminLayout from "../components/AdminLayout";
import { fetchVehicles } from "../actions/VehicleActions";
import { 
  Search, 
  X, 
  RefreshCw, 
  MapPin, 
  Truck, 
  Navigation,
  Filter,
  ChevronRight,
  User,
  Circle,
  Crosshair,
  Grid,
  Table,
  ChevronDown,
  ChevronUp
} from "lucide-react";

// Vehicle icons
import CarIcon from "../assets/car.png";
import VanIcon from "../assets/van.png";
import BusIcon from "../assets/bus.png";
import SuvIcon from "../assets/suv.png";
import CabIcon from "../assets/cab.png";
import ForkliftIcon from "../assets/fork lift.png";
import LorryIcon from "../assets/lorry.png";
import CherryIcon from "../assets/cherry.png";
import CraneIcon from "../assets/crane.jpg";

const TYPE_ICONS = {
  C: CarIcon,
  V: VanIcon,
  B: BusIcon,
  S: SuvIcon,
  CA: CabIcon,
  F: ForkliftIcon,
  L: LorryIcon,
  CH: CherryIcon,
  T: LorryIcon,
  CR: CraneIcon,
};

const createIcon = (code, selected = false) => {
  const url = TYPE_ICONS[code] || TYPE_ICONS.C;
  return new L.Icon({
    iconUrl: url,
    iconSize: selected ? [46, 46] : [36, 36],
    iconAnchor: [selected ? 23 : 18, selected ? 46 : 36],
    popupAnchor: [0, selected ? -46 : -36],
  });
};

const getVehicleCode = (type) => {
  if (!type) return "C";
  const t = type.toString().trim().toLowerCase();
  if (t.includes("car")) return "C";
  if (t.includes("van")) return "V";
  if (t.includes("bus")) return "B";
  if (t.includes("suv")) return "S";
  if (t.includes("cab")) return "CA";
  if (t.includes("forklift")) return "F";
  if (t.includes("cherry")) return "CH";
  if (t.includes("trailer") || t === "t") return "T";
  if (t.includes("crane") || t === "cr") return "CR";
  if (t.includes("lorry")) return "L";
  return "C";
};

const VEHICLE_FILTERS = [
  { code: "ALL", label: "All", icon: TYPE_ICONS.C },
  { code: "C", label: "Car", icon: TYPE_ICONS.C },
  { code: "V", label: "Van", icon: TYPE_ICONS.V },
  { code: "B", label: "Bus", icon: TYPE_ICONS.B },
  { code: "S", label: "SUV", icon: TYPE_ICONS.S },
  { code: "CA", label: "Cab", icon: TYPE_ICONS.CA },
  { code: "F", label: "Forklift", icon: TYPE_ICONS.F },
  { code: "L", label: "Lorry", icon: TYPE_ICONS.L },
  { code: "CH", label: "Cherry-Picker", icon: TYPE_ICONS.CH },
  { code: "T", label: "Trailer", icon: TYPE_ICONS.T },
  { code: "CR", label: "Crane", icon: TYPE_ICONS.CR },
];

// Fix leaflet icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png").default,
  iconUrl: require("leaflet/dist/images/marker-icon.png").default,
  shadowUrl: require("leaflet/dist/images/marker-shadow.png").default,
});

// Auto-pan to selected vehicle
function PanToVehicle({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.flyTo(position, 15, { duration: 1.0 });
    }
  }, [position, map]);
  return null;
}

// Desktop Vehicle Card Component
function DesktopVehicleCard({ vehicle, isSelected, onSelect, currentPosition }) {
  const code = getVehicleCode(vehicle.vehicle_type || vehicle.vehicleType);
  const lat = parseFloat(vehicle.latitude);
  const lng = parseFloat(vehicle.longitude);
  
  const position = [
    !isNaN(lat) && lat !== 0 ? lat : currentPosition[0],
    !isNaN(lng) && lng !== 0 ? lng : currentPosition[1],
  ];
  
  return (
    <div
      onClick={() => onSelect(vehicle)}
      className={`p-3 rounded-lg cursor-pointer transition-all ${
        isSelected
          ? "bg-blue-50 border-2 border-blue-500"
          : "bg-white border border-gray-200 hover:border-gray-300 hover:shadow-sm"
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2 flex-1">
          <img 
            src={TYPE_ICONS[code] || TYPE_ICONS.C} 
            alt={vehicle.vehicleType} 
            className="w-8 h-8 object-contain"
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">
              {vehicle.vehicleType} • {vehicle.vehicleId}
            </p>
            <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
              <User size={10} />
              <span className="truncate">{vehicle.driverName || vehicle.driverSno || "No driver"}</span>
            </div>
          </div>
        </div>
        <div className="text-xs text-gray-400">
          {position[0].toFixed(4)}, {position[1].toFixed(4)}
        </div>
      </div>
      
      {isSelected && (
        <div className="mt-2 pt-2 border-t border-blue-100">
          <div className="flex items-center gap-2 text-xs text-blue-700">
            <Navigation size={10} />
            <span>Selected vehicle</span>
          </div>
        </div>
      )}
    </div>
  );
}

// Mobile Vehicle Card Component
function MobileVehicleCard({ vehicle, isSelected, onSelect, currentPosition, index }) {
  const code = getVehicleCode(vehicle.vehicle_type || vehicle.vehicleType);
  const lat = parseFloat(vehicle.latitude);
  const lng = parseFloat(vehicle.longitude);
  
  const position = [
    !isNaN(lat) && lat !== 0 ? lat : currentPosition[0],
    !isNaN(lng) && lng !== 0 ? lng : currentPosition[1],
  ];
  
  return (
    <div
      onClick={() => onSelect(vehicle)}
      className={`p-3 rounded-lg cursor-pointer transition-all ${
        isSelected
          ? "bg-blue-50 border-l-4 border-blue-500"
          : "bg-white border border-gray-200 active:bg-gray-50"
      }`}
    >
      <div className="flex items-center gap-3">
        <img 
          src={TYPE_ICONS[code] || TYPE_ICONS.C} 
          alt={vehicle.vehicleType} 
          className="w-10 h-10 object-contain"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-900 truncate">
              {vehicle.vehicleId}
            </p>
            <span className="text-xs text-gray-400">
              {position[0].toFixed(4)}°, {position[1].toFixed(4)}°
            </span>
          </div>
          <p className="text-xs text-gray-600">{vehicle.vehicleType}</p>
          <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
            <User size={10} />
            <span className="truncate">{vehicle.driverName || vehicle.driverSno || "No driver"}</span>
          </div>
          <div className="mt-2">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
              vehicle.status === 'Active' 
                ? 'bg-emerald-50 text-emerald-700' 
                : 'bg-gray-100 text-gray-600'
            }`}>
              {vehicle.status}
            </span>
          </div>
        </div>
        {isSelected && (
          <Navigation size={16} className="text-blue-600 flex-shrink-0" />
        )}
      </div>
    </div>
  );
}

// Filter Button Component
function FilterButton({ filterItem, isActive, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-all flex-shrink-0 ${
        isActive
          ? "bg-gray-900 text-white shadow-sm"
          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
      }`}
    >
      <img src={filterItem.icon} alt={filterItem.label} className="w-3.5 h-3.5" />
      <span className="hidden sm:inline">{filterItem.label}</span>
      <span className="sm:hidden">{filterItem.label.charAt(0)}</span>
    </button>
  );
}

export default function AdminMap() {
  const dispatch = useDispatch();
  const { authKey } = useSelector((state) => state.auth || {});
  const { items: allVehicles, loading: loadingVehicles } = useSelector(
    (state) => state.vehicle,
  );

  const [filter, setFilter] = useState("ALL");
  const [currentPosition, setCurrentPosition] = useState([6.9271, 79.8612]);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [selectedVehiclePosition, setSelectedVehiclePosition] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState('table');
  const [showFilters, setShowFilters] = useState(false);
  const mapRef = useRef(null);
  const pollingRef = useRef(null);

  // Detect screen size for default view
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setViewMode('card');
      } else {
        setViewMode('table');
      }
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchVehiclesData = async () => {
    await dispatch(fetchVehicles());
  };

  // Fetch vehicles every 5 sec
  useEffect(() => {
    fetchVehiclesData();
    pollingRef.current = setInterval(fetchVehiclesData, 5000);

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [authKey, dispatch]);

  // Active vehicles with assigned drivers
  const vehicles = useMemo(
    () =>
      (allVehicles || []).filter((v) => {
        if (!v) return false;
        const isActive =
          v.status?.toLowerCase() === "active" ||
          v.status?.toLowerCase() === "online";
        const hasAssignedDriver = Boolean(
          (v.driverSno && String(v.driverSno).trim() !== "") ||
          (v.driverName && String(v.driverName).trim() !== ""),
        );
        return isActive && hasAssignedDriver;
      }),
    [allVehicles],
  );

  // Get current user location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) =>
          setCurrentPosition([pos.coords.latitude, pos.coords.longitude]),
        () => {},
        { enableHighAccuracy: true },
      );
    }
  }, []);

  // Filters
  const filteredVehicles = useMemo(() => {
    return vehicles.filter((v) => {
      const code = getVehicleCode(v.vehicle_type || v.vehicleType);
      if (filter !== "ALL" && code !== filter) return false;
      return true;
    });
  }, [vehicles, filter]);

  // Search
  const searchedVehicles = useMemo(() => {
    const q = (searchQuery || "").trim().toLowerCase();
    if (!q) return filteredVehicles;

    return filteredVehicles.filter((v) => {
      const vid = (v.vehicleId || "").toLowerCase();
      const dsno = (v.driverSno || "").toLowerCase();
      const dname = (v.driverName || "").toLowerCase();
      return vid.includes(q) || dsno.includes(q) || dname.includes(q);
    });
  }, [filteredVehicles, searchQuery]);

  useEffect(() => {
    if (searchedVehicles.length === 1 && searchQuery) {
      const v = searchedVehicles[0];
      setSelectedVehicleId(v.vehicleId);
      const lat = parseFloat(v.latitude);
      const lng = parseFloat(v.longitude);
      setSelectedVehiclePosition([
        !isNaN(lat) && lat !== 0 ? lat : currentPosition[0],
        !isNaN(lng) && lng !== 0 ? lng : currentPosition[1],
      ]);
    }

    if (!searchQuery) {
      setSelectedVehicleId(null);
      setSelectedVehiclePosition(null);
    }
  }, [searchQuery, searchedVehicles.length, currentPosition]);

  const handleSelectVehicle = (v) => {
    setSelectedVehicleId(v.vehicleId);
    const lat = parseFloat(v.latitude);
    const lng = parseFloat(v.longitude);
    setSelectedVehiclePosition([
      !isNaN(lat) && lat !== 0 ? lat : currentPosition[0],
      !isNaN(lng) && lng !== 0 ? lng : currentPosition[1],
    ]);
  };

  const handleRefresh = () => {
    setFilter("ALL");
    setSearchQuery("");
    setSelectedVehicleId(null);
    setSelectedVehiclePosition(null);
    fetchVehiclesData();
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSelectedVehicleId(null);
    setSelectedVehiclePosition(null);
  };

  // Stats
  const totalVehicles = vehicles.length;
  const filteredCount = searchedVehicles.length;

  return (
    <AdminLayout>
      {/* Header Section */}
      <div className="mb-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center gap-2">
              <MapPin size={20} />
              Vehicle Tracking
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">
              Real-time vehicle locations and fleet monitoring
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-lg border border-emerald-200">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></div>
              Live Tracking
            </span>
            <button
              onClick={handleRefresh}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
            >
              <RefreshCw size={12} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <Truck size={14} className="text-blue-600" />
            <span className="text-xs text-gray-600">Total Vehicles</span>
          </div>
          <p className="text-xl font-semibold text-gray-900">{totalVehicles}</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
          <div className="flex items-center gap-2 mb-1">
            <Navigation size={14} className="text-emerald-600" />
            <span className="text-xs text-gray-600">Active on Map</span>
          </div>
          <p className="text-xl font-semibold text-gray-900">{filteredCount}</p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white border border-gray-100 rounded-lg shadow-sm p-3 mb-4">
        {/* Search Bar */}
        <div className="relative mb-3">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by vehicle ID or driver name..."
            className="w-full pl-8 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-400 transition"
          />
          {searchQuery && (
            <button
              onClick={clearSearch}
              className="absolute right-2.5 top-1/2 -translate-y-1/2"
            >
              <X size={12} className="text-gray-400 hover:text-gray-600" />
            </button>
          )}
        </div>

        {/* Filter Toggle for Mobile */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="md:hidden w-full flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg mb-2"
        >
          <div className="flex items-center gap-2">
            <Filter size={14} className="text-gray-500" />
            <span className="text-xs font-medium text-gray-700">Filter by vehicle type</span>
          </div>
          {showFilters ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>

        {/* Filters */}
        <div className={`${showFilters ? 'block' : 'hidden'} md:block`}>
          <div className="flex items-center gap-1.5 mb-2">
            <Filter size={12} className="text-gray-400 hidden md:block" />
            <span className="text-xs text-gray-500 hidden md:block">Filter by type</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {VEHICLE_FILTERS.map((f) => (
              <FilterButton
                key={f.code}
                filterItem={f}
                isActive={filter === f.code}
                onClick={() => setFilter(f.code)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* View Toggle for Desktop */}
      <div className="hidden md:flex justify-end mb-3">
        <div className="flex gap-1 bg-gray-100 p-0.5 rounded-lg">
          <button
            onClick={() => setViewMode('table')}
            className={`px-2.5 py-1 text-xs font-medium rounded transition ${
              viewMode === 'table' 
                ? 'bg-white text-gray-900 shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Table size={12} className="inline mr-1" />
            Split View
          </button>
          <button
            onClick={() => setViewMode('card')}
            className={`px-2.5 py-1 text-xs font-medium rounded transition ${
              viewMode === 'card' 
                ? 'bg-white text-gray-900 shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Grid size={12} className="inline mr-1" />
            Full Map
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Desktop: Split View - Vehicle List */}
        {viewMode === 'table' && (
          <div className="hidden lg:block lg:col-span-4">
            <div className="bg-white border border-gray-100 rounded-lg shadow-sm overflow-hidden">
              <div className="p-3 border-b border-gray-100 bg-gray-50">
                <h2 className="text-sm font-semibold text-gray-900">
                  Vehicles on Map ({filteredCount})
                </h2>
              </div>
              <div className="max-h-[calc(100vh-380px)] overflow-y-auto p-3 space-y-2">
                {loadingVehicles ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw size={16} className="text-gray-400 animate-spin" />
                    <span className="ml-2 text-xs text-gray-500">Loading...</span>
                  </div>
                ) : searchedVehicles.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="flex flex-col items-center gap-1.5">
                      <Truck size={24} className="text-gray-300" />
                      <p className="text-xs text-gray-500">No vehicles found</p>
                    </div>
                  </div>
                ) : (
                  searchedVehicles.map((v) => (
                    <DesktopVehicleCard
                      key={v.vehicleId}
                      vehicle={v}
                      isSelected={selectedVehicleId === v.vehicleId}
                      onSelect={handleSelectVehicle}
                      currentPosition={currentPosition}
                    />
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* Map Panel */}
        <div className={`${viewMode === 'table' ? 'lg:col-span-8' : 'lg:col-span-12'} col-span-1 transition-all duration-300`}>
          <div className="bg-white border border-gray-100 rounded-lg shadow-sm overflow-hidden">
            <div className="p-3 border-b border-gray-100 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-blue-50 rounded-lg">
                    <MapPin size={14} className="text-blue-600" />
                  </div>
                  <h2 className="text-sm font-semibold text-gray-900">
                    Live Map View
                  </h2>
                </div>
                <div className="flex items-center gap-1 text-xs text-gray-500">
                  <Circle size={8} className="fill-blue-500 text-blue-500" />
                  <span className="hidden sm:inline">Your Location</span>
                  <span className="sm:mx-1 mx-0.5">•</span>
                  <Truck size={8} />
                  <span className="hidden sm:inline">Vehicles</span>
                </div>
              </div>
            </div>
            
            <div className="h-[calc(100vh-480px)] min-h-[400px] w-full">
              <MapContainer
                center={currentPosition}
                zoom={13}
                className="h-full w-full"
                whenCreated={(map) => (mapRef.current = map)}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                />
                
                <RecenterButton position={currentPosition} zoom={13} />
                
                {selectedVehiclePosition && (
                  <PanToVehicle position={selectedVehiclePosition} />
                )}
                
                <CircleMarker
                  center={currentPosition}
                  radius={8}
                  color="#3B82F6"
                  fillColor="#3B82F6"
                  fillOpacity={0.7}
                  weight={2}
                >
                  <Popup>
                    <div className="text-sm">
                      <p className="font-semibold">Your Location</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {currentPosition[0].toFixed(6)}, {currentPosition[1].toFixed(6)}
                      </p>
                    </div>
                  </Popup>
                </CircleMarker>
                
                {searchedVehicles.map((v) => {
                  const code = getVehicleCode(v.vehicle_type || v.vehicleType);
                  const isSelected = v.vehicleId === selectedVehicleId;
                  const icon = createIcon(code, isSelected);
                  
                  const lat = parseFloat(v.latitude);
                  const lng = parseFloat(v.longitude);
                  const position = [
                    !isNaN(lat) && lat !== 0 ? lat : currentPosition[0],
                    !isNaN(lng) && lng !== 0 ? lng : currentPosition[1],
                  ];
                  
                  return (
                    <Marker
                      key={v.vehicleId}
                      position={position}
                      icon={icon}
                      eventHandlers={{
                        click: () => handleSelectVehicle(v),
                      }}
                    >
                      <Popup>
                        <div className="min-w-[180px]">
                          <div className="flex items-center gap-2 mb-2">
                            <img 
                              src={TYPE_ICONS[code] || TYPE_ICONS.C} 
                              alt={v.vehicleType} 
                              className="w-8 h-8 object-contain"
                            />
                            <div>
                              <p className="font-semibold text-gray-900">
                                {v.vehicleType}
                              </p>
                              <p className="text-xs text-gray-500">
                                {v.vehicleId}
                              </p>
                            </div>
                          </div>
                          <div className="space-y-1 text-xs">
                            <p>
                              <span className="text-gray-500">Driver:</span>{' '}
                              <span className="font-medium">{v.driverName || v.driverSno || '—'}</span>
                            </p>
                            <p>
                              <span className="text-gray-500">Status:</span>{' '}
                              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs ${
                                v.status === 'Active' 
                                  ? 'bg-emerald-50 text-emerald-700' 
                                  : 'bg-gray-100 text-gray-600'
                              }`}>
                                {v.status}
                              </span>
                            </p>
                            <p className="text-gray-400 text-[10px] mt-1">
                              {position[0].toFixed(6)}, {position[1].toFixed(6)}
                            </p>
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}
              </MapContainer>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile: Vehicle List Bottom Sheet */}
      {viewMode === 'card' && (
        <div className="lg:hidden mt-4">
          <div className="bg-white border border-gray-100 rounded-lg shadow-sm overflow-hidden">
            <div className="p-3 border-b border-gray-100 bg-gray-50">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-900">
                  Vehicles ({filteredCount})
                </h2>
                <span className="text-xs text-gray-500">
                  Tap to select
                </span>
              </div>
            </div>
            <div className="max-h-[300px] overflow-y-auto p-3 space-y-2">
              {loadingVehicles ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw size={16} className="text-gray-400 animate-spin" />
                  <span className="ml-2 text-xs text-gray-500">Loading...</span>
                </div>
              ) : searchedVehicles.length === 0 ? (
                <div className="text-center py-8">
                  <div className="flex flex-col items-center gap-1.5">
                    <Truck size={24} className="text-gray-300" />
                    <p className="text-xs text-gray-500">No vehicles found</p>
                  </div>
                </div>
              ) : (
                searchedVehicles.map((v, index) => (
                  <MobileVehicleCard
                    key={v.vehicleId}
                    vehicle={v}
                    isSelected={selectedVehicleId === v.vehicleId}
                    onSelect={handleSelectVehicle}
                    currentPosition={currentPosition}
                    index={index}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}